import React, { useState } from 'react';
import {
  Users, UserCheck, Calendar as CalendarIcon, TrendingUp,
  Clock, Coffee, LogIn, LogOut, CheckCircle2,
  Send, Sparkles, Bot,
  Utensils, Plus, Minus, ShoppingCart, Trash2
} from 'lucide-react';

// Shared little helper
const StatCard = ({ icon: Icon, label, value, hint, tone = 'blue' }) => {
  const tones = {
    blue: 'text-blue-500 bg-blue-500/10',
    emerald: 'text-emerald-500 bg-emerald-500/10',
    orange: 'text-orange-500 bg-orange-500/10',
    purple: 'text-purple-500 bg-purple-500/10',
  };
  return (
    <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-4">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${tones[tone]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-gray-400 mt-0.5">{label}</div>
      {hint && <div className="text-[10px] text-emerald-400 mt-1">{hint}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 1) EmployeeHub — HR dashboard
// ---------------------------------------------------------------------------
export const EmployeeHubDemo = () => {
  const [employees, setEmployees] = useState([
    { id: 1, name: 'Anna Meier', role: 'Frontend Dev', checkin: '08:12', status: 'present' },
    { id: 2, name: 'Luca Rossi', role: 'Sales Manager', checkin: '09:04', status: 'present' },
    { id: 3, name: 'Marie Dubois', role: 'Designer', checkin: '—', status: 'leave' },
    { id: 4, name: 'Tom Weber', role: 'Backend Dev', checkin: '07:58', status: 'present' },
    { id: 5, name: 'Sara Keller', role: 'HR Specialist', checkin: '08:30', status: 'present' },
  ]);

  const toggle = (id) =>
    setEmployees((prev) =>
      prev.map((e) =>
        e.id === id ? { ...e, status: e.status === 'present' ? 'leave' : 'present' } : e
      )
    );

  const present = employees.filter((e) => e.status === 'present').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total Employees" value={employees.length} tone="blue" />
        <StatCard icon={UserCheck} label="Present Today" value={present} hint="+2 vs yesterday" tone="emerald" />
        <StatCard icon={CalendarIcon} label="On Leave" value={employees.length - present} tone="orange" />
        <StatCard icon={TrendingUp} label="Productivity" value="92%" hint="+4% this week" tone="purple" />
      </div>

      <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-white">Today&apos;s Attendance</h4>
          <span className="text-[10px] text-gray-500">Click any row to toggle status</span>
        </div>
        <div className="divide-y divide-slate-800">
          {employees.map((e) => (
            <button
              key={e.id}
              onClick={() => toggle(e.id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                  {e.name.split(' ').map((n) => n[0]).join('')}
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{e.name}</div>
                  <div className="text-xs text-gray-500">{e.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 tabular-nums">{e.checkin}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    e.status === 'present'
                      ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                      : 'bg-orange-500/15 text-orange-400 border border-orange-500/30'
                  }`}
                >
                  {e.status === 'present' ? 'PRESENT' : 'ON LEAVE'}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2) EmployeeHub Mini — Clock-in interface
// ---------------------------------------------------------------------------
export const EmployeeHubMiniDemo = () => {
  const [clockedIn, setClockedIn] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [log, setLog] = useState([
    { id: 1, action: 'Check-in', time: '08:15', date: 'Yesterday' },
    { id: 2, action: 'Check-out', time: '17:32', date: 'Yesterday' },
  ]);

  const toggle = () => {
    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    if (!clockedIn) {
      setStartTime(now);
      setLog((p) => [{ id: Date.now(), action: 'Check-in', time: timeStr, date: 'Just now' }, ...p]);
    } else {
      setLog((p) => [{ id: Date.now(), action: 'Check-out', time: timeStr, date: 'Just now' }, ...p]);
      setStartTime(null);
    }
    setClockedIn((v) => !v);
  };

  const elapsed = startTime
    ? Math.floor((Date.now() - startTime.getTime()) / 60000)
    : 0;

  return (
    <div className="space-y-4">
      <div
        className={`rounded-2xl p-6 text-center transition-all duration-500 ${
          clockedIn
            ? 'bg-gradient-to-br from-emerald-500/20 to-emerald-700/10 border border-emerald-500/40'
            : 'bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700'
        }`}
      >
        <div className="flex items-center justify-center mb-3">
          <div
            className={`w-14 h-14 rounded-full flex items-center justify-center ${
              clockedIn ? 'bg-emerald-500/30' : 'bg-slate-700/50'
            }`}
          >
            <Clock className={`w-6 h-6 ${clockedIn ? 'text-emerald-300' : 'text-gray-400'}`} />
          </div>
        </div>
        <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">
          {clockedIn ? 'Currently working' : 'Not clocked in'}
        </div>
        <div className="text-3xl font-bold text-white mb-4">
          {clockedIn ? `${Math.max(0, elapsed)} min today` : '00:00'}
        </div>
        <button
          onClick={toggle}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold shadow-lg transition-all ${
            clockedIn
              ? 'bg-orange-500 hover:bg-orange-600 text-white'
              : 'bg-emerald-500 hover:bg-emerald-600 text-white'
          }`}
        >
          {clockedIn ? <LogOut className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
          {clockedIn ? 'Check Out' : 'Check In'}
        </button>
      </div>

      <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl">
        <div className="px-4 py-3 border-b border-slate-700/50 flex items-center gap-2">
          <Coffee className="w-4 h-4 text-blue-400" />
          <h4 className="text-sm font-semibold text-white">Recent Activity</h4>
        </div>
        <div className="divide-y divide-slate-800">
          {log.slice(0, 5).map((l) => (
            <div key={l.id} className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <CheckCircle2
                  className={`w-4 h-4 ${
                    l.action === 'Check-in' ? 'text-emerald-400' : 'text-orange-400'
                  }`}
                />
                <span className="text-sm text-white">{l.action}</span>
              </div>
              <div className="text-xs text-gray-400 tabular-nums">
                {l.time} <span className="text-gray-600">• {l.date}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 3) KosovAI — Chatbot interface
// ---------------------------------------------------------------------------
const KOSOVAI_REPLIES = {
  'hello': 'Përshëndetje! Si mund t\'ju ndihmoj sot?',
  'default': 'That\'s a great question! In a real deployment I\'d connect to your knowledge base and give a precise answer.',
  'features': 'I can answer FAQs, book meetings, qualify leads, translate on the fly (SQ/EN/DE), and hand off to a human when needed.',
  'price': 'Pricing depends on volume — but most Swiss SMBs are between CHF 149–499/month for hosted AI.',
};
const pickReply = (msg) => {
  const m = msg.toLowerCase();
  if (m.includes('hi') || m.includes('hello') || m.includes('përshëndetje')) return KOSOVAI_REPLIES.hello;
  if (m.includes('feature') || m.includes('do')) return KOSOVAI_REPLIES.features;
  if (m.includes('price') || m.includes('cost') || m.includes('chf')) return KOSOVAI_REPLIES.price;
  return KOSOVAI_REPLIES.default;
};

export const KosovAIDemo = () => {
  const [messages, setMessages] = useState([
    { id: 1, from: 'bot', text: 'Hi! I\'m KosovAI — an Albanian-focused AI assistant. Ask me anything!' },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);

  const send = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    const userMsg = { id: Date.now(), from: 'user', text: trimmed };
    setMessages((p) => [...p, userMsg]);
    setInput('');
    setTyping(true);
    setTimeout(() => {
      setMessages((p) => [...p, { id: Date.now() + 1, from: 'bot', text: pickReply(trimmed) }]);
      setTyping(false);
    }, 800);
  };

  return (
    <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-700/50 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="text-sm font-semibold text-white flex items-center gap-1.5">
            KosovAI <Sparkles className="w-3 h-3 text-yellow-400" />
          </div>
          <div className="text-[10px] text-emerald-400">● online</div>
        </div>
      </div>
      <div className="p-4 h-72 overflow-y-auto space-y-3 bg-slate-950/40">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                m.from === 'user'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-slate-800 text-gray-200 rounded-bl-sm'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {typing && (
          <div className="flex justify-start">
            <div className="bg-slate-800 text-gray-200 px-3 py-2 rounded-2xl rounded-bl-sm">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"></div>
                <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="p-3 border-t border-slate-700/50 flex gap-2 bg-slate-900/60">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Ask something..."
          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-500"
        />
        <button
          onClick={send}
          className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 4) Restaurant POS — Point of sale
// ---------------------------------------------------------------------------
const MENU = [
  { id: 'pizza', name: 'Margherita', price: 16.5, emoji: '🍕' },
  { id: 'pasta', name: 'Carbonara', price: 19.9, emoji: '🍝' },
  { id: 'salad', name: 'Caesar Salad', price: 14.0, emoji: '🥗' },
  { id: 'burger', name: 'Cheeseburger', price: 18.5, emoji: '🍔' },
  { id: 'coffee', name: 'Espresso', price: 4.5, emoji: '☕' },
  { id: 'wine', name: 'House Wine', price: 8.5, emoji: '🍷' },
];

export const RestaurantPOSDemo = () => {
  const [cart, setCart] = useState({});

  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const remove = (id) =>
    setCart((c) => {
      const next = { ...c };
      if (!next[id]) return c;
      next[id] -= 1;
      if (next[id] <= 0) delete next[id];
      return next;
    });
  const clear = () => setCart({});

  const items = Object.entries(cart).map(([id, qty]) => ({
    ...MENU.find((m) => m.id === id),
    qty,
  }));
  const subtotal = items.reduce((s, it) => s + it.price * it.qty, 0);
  const tax = subtotal * 0.077; // Swiss VAT
  const total = subtotal + tax;

  const fmt = (n) => n.toFixed(2).replace('.', '.');

  return (
    <div className="grid md:grid-cols-5 gap-4">
      <div className="md:col-span-3 bg-slate-900/60 border border-slate-700/50 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Utensils className="w-4 h-4 text-orange-400" />
          <h4 className="text-sm font-semibold text-white">Menu</h4>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {MENU.map((m) => (
            <button
              key={m.id}
              onClick={() => add(m.id)}
              className="flex items-center gap-3 p-3 bg-slate-800/60 hover:bg-slate-800 border border-slate-700 hover:border-orange-500/50 rounded-lg transition-all text-left"
            >
              <span className="text-2xl">{m.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{m.name}</div>
                <div className="text-xs text-orange-400 font-semibold">CHF {fmt(m.price)}</div>
              </div>
              <Plus className="w-4 h-4 text-gray-400" />
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2 bg-slate-900/60 border border-slate-700/50 rounded-xl p-4 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-orange-400" />
            <h4 className="text-sm font-semibold text-white">Order</h4>
          </div>
          {items.length > 0 && (
            <button onClick={clear} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        <div className="flex-1 space-y-1.5 min-h-[10rem]">
          {items.length === 0 && (
            <div className="text-xs text-gray-500 text-center py-8">
              Click items to add to the order
            </div>
          )}
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between bg-slate-800/50 rounded px-2 py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <span>{it.emoji}</span>
                <span className="text-xs text-white truncate">{it.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => remove(it.id)} className="text-gray-400 hover:text-white">
                  <Minus className="w-3 h-3" />
                </button>
                <span className="text-xs font-bold text-white w-4 text-center">{it.qty}</span>
                <button onClick={() => add(it.id)} className="text-gray-400 hover:text-white">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-slate-700/50 space-y-1">
          <div className="flex justify-between text-xs text-gray-400">
            <span>Subtotal</span>
            <span className="tabular-nums">CHF {fmt(subtotal)}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>VAT 7.7%</span>
            <span className="tabular-nums">CHF {fmt(tax)}</span>
          </div>
          <div className="flex justify-between text-base font-bold text-white pt-1">
            <span>Total</span>
            <span className="tabular-nums text-orange-400">CHF {fmt(total)}</span>
          </div>
          <button
            disabled={items.length === 0}
            className="w-full mt-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 disabled:text-gray-500 text-white font-semibold py-2 rounded-lg transition-colors"
          >
            Charge Order
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Registry: map project id → demo component
// ---------------------------------------------------------------------------
export const PROJECT_DEMOS = {
  1: EmployeeHubDemo,        // EmployeeHub
  2: EmployeeHubMiniDemo,    // EmployeeHub Mini
  3: KosovAIDemo,            // KosovAI
  4: RestaurantPOSDemo,      // Restaurant Point App
};
